/**
 * Driver QR Scan Page
 * Scan QR codes for package pickup, handoff, and delivery confirmation
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  QrCode,
  Camera,
  CheckCircle,
  AlertCircle,
  Package,
  Truck,
  MapPin
} from 'lucide-react';

const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
};

const DriverScan = () => {
  const navigate = useNavigate();
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [scanType, setScanType] = useState('pickup'); // pickup, handoff, delivery
  const inputRef = useRef(null);

  // For demo - manual QR input
  const [manualCode, setManualCode] = useState('');

  const scanTypes = [
    { id: 'pickup', label: 'Start Pickup', icon: Package, color: COLORS.green },
    { id: 'handoff', label: 'Chain Handoff', icon: Truck, color: COLORS.yellow },
    { id: 'delivery', label: 'Final Delivery', icon: MapPin, color: COLORS.black },
  ];

  const handleScan = async (qrData) => {
    if (!qrData || processing) return;
    
    setProcessing(true);
    try {
      const response = await api.post('/drivers/scan', null, {
        params: { qr_data: qrData, scan_type: scanType }
      });
      
      setScanResult({
        success: true,
        message: response.data.message || 'Scan successful!',
        data: response.data
      });
      
      toast.success(response.data.message || 'QR Code verified!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Scan failed';
      setScanResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
      setManualCode('');
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
    }
  };

  const resetScan = () => {
    setScanResult(null);
    setManualCode('');
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #E8FFF4 100%)' }}>
      {/* Header */}
      <header className="bg-white px-5 py-4 shadow-sm flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/driver')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Button>
        <div className="flex items-center gap-2">
          <QrCode className="h-6 w-6 text-[#00A86B]" />
          <h1 className="text-lg font-bold text-gray-800">Scan Package QR</h1>
        </div>
      </header>

      <main className="p-5 space-y-6">
        {/* Scan Type Selector */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <p className="text-sm text-gray-500 mb-3">Select scan type:</p>
          <div className="grid grid-cols-3 gap-2">
            {scanTypes.map((type) => {
              const Icon = type.icon;
              const isActive = scanType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => { setScanType(type.id); resetScan(); }}
                  className={`p-3 rounded-xl transition-all ${
                    isActive 
                      ? 'text-white shadow-lg scale-105' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={isActive ? { background: type.color } : {}}
                >
                  <Icon className="h-5 w-5 mx-auto mb-1" />
                  <span className="text-xs font-medium block">{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scanner Area */}
        {!scanResult && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="relative aspect-square max-w-xs mx-auto bg-gray-900 rounded-2xl overflow-hidden flex items-center justify-center">
              {/* Camera placeholder */}
              <div className="text-center text-white">
                <Camera className="h-16 w-16 mx-auto mb-3 opacity-50" />
                <p className="text-sm opacity-70">Camera not available</p>
                <p className="text-xs opacity-50 mt-1">Use manual entry below</p>
              </div>
              
              {/* Scanner frame overlay */}
              <div className="absolute inset-8 border-2 border-[#00A86B] rounded-xl">
                <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FFD300] rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FFD300] rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FFD300] rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FFD300] rounded-br-lg" />
              </div>
              
              {/* Scanning animation line */}
              {scanning && (
                <div className="absolute left-8 right-8 h-0.5 bg-[#00A86B] animate-bounce" />
              )}
            </div>
            
            {/* Manual Entry */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 text-center mb-3">Or enter QR code manually:</p>
              <form onSubmit={handleManualSubmit} className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Enter package code..."
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#00A86B]"
                />
                <Button
                  type="submit"
                  disabled={!manualCode.trim() || processing}
                  className="px-6 text-white rounded-xl"
                  style={{ background: COLORS.green }}
                >
                  {processing ? 'Checking...' : 'Verify'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Scan Result */}
        {scanResult && (
          <div className={`bg-white rounded-2xl p-6 shadow-sm border-2 ${
            scanResult.success ? 'border-[#00A86B]' : 'border-red-400'
          }`}>
            <div className="text-center">
              {scanResult.success ? (
                <CheckCircle className="h-16 w-16 text-[#00A86B] mx-auto mb-4" />
              ) : (
                <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              )}
              
              <h3 className={`text-xl font-bold mb-2 ${
                scanResult.success ? 'text-[#00A86B]' : 'text-red-500'
              }`}>
                {scanResult.success ? 'Success!' : 'Scan Failed'}
              </h3>
              
              <p className="text-gray-600 mb-6">{scanResult.message}</p>
              
              {scanResult.success && scanResult.data?.package && (
                <div className="bg-gray-50 rounded-xl p-4 text-left mb-6">
                  <p className="text-sm text-gray-500">Package Details:</p>
                  <p className="font-medium">{scanResult.data.package.recipient_name}</p>
                  <p className="text-sm text-gray-600">
                    {scanResult.data.package.dropoff_location?.city || 'Destination'}
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={resetScan}
                  variant="outline"
                  className="flex-1 rounded-xl border-gray-300"
                >
                  Scan Another
                </Button>
                <Button
                  onClick={() => navigate('/driver')}
                  className="flex-1 rounded-xl text-white"
                  style={{ background: COLORS.green }}
                >
                  Back to Jobs
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-white/80 rounded-2xl p-4">
          <h4 className="font-medium text-gray-800 mb-2">Instructions:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>Pickup:</strong> Scan when collecting package from sender</li>
            <li>• <strong>Handoff:</strong> Scan when transferring to next driver (chain delivery)</li>
            <li>• <strong>Delivery:</strong> Scan when handing package to recipient</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default DriverScan;
